import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ParkbaseStorageService } from '../../services/parkbaseStorage.service';
import { OccupationPerHour } from '../../../@core/domain/interfaces/occupation.interface';
import dayjs, { Dayjs } from 'dayjs';
import { FacilitiesApiService } from '../../../@core/api/facilities-api.service';
import { default as defaultChartOptions } from './chart-defaults.json';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-activity-chart',
  templateUrl: './activity-chart.component.html',
  styleUrls: ['./activity-chart.component.scss'],
})
export class ActivityChartComponent implements OnInit, OnDestroy {
  @Input()
  facilityId!: number;

  @Input()
  style!: string;

  @Input()
  showNavigation = true;

  @Input()
  showDate = false;

  historyDataSubscription$!: Subscription;

  currentDate = dayjs();
  showForward = false;
  options: any;
  updateOptions: any;
  echartsInstance: any;

  colorDark = '#4a5b65';
  colorLight = '#e8f1f2';

  initOps: any = {
    renderer: 'svg',
  };

  colorOne = 'rgba(12,206,107,1.0)';
  colorTwo = 'rgba(249,200,14,1.0)';
  colorThree = 'rgba(255,69,14,1.0)';

  colors = [this.colorOne, this.colorTwo, this.colorThree];

  interval: any;

  constructor(
    private parkbaseStorageService: ParkbaseStorageService,
    private facilitiesApiService: FacilitiesApiService
  ) {}

  ngOnInit(): void {
    const backgroundStyle = this.getBackGroundStyle();

    this.options = defaultChartOptions;
    this.options.series[0]['backgroundStyle'] = backgroundStyle;
    this.updateOptions = {
      series: [
        {
          data: [],
        },
      ],
    };

    this.getHistoryData(dayjs());
  }

  ngOnDestroy() {
    this.historyDataSubscription$.unsubscribe();
    this.interval = null;
    this.options = [];
    this.updateOptions = [];
    this.echartsInstance = null;
  }

  onChartInit(e: any) {
    this.echartsInstance = e;
    if (this.echartsInstance) {
      this.interval = setInterval(() => {
        this.getHistoryData(dayjs());
      }, 120000);
    }
  }

  goBack(): void {
    this.currentDate = this.currentDate.subtract(1, 'day');
    this.getHistoryData(this.currentDate);
    this.echartsInstance.resize(600, 300);
  }

  goForward(): void {
    this.currentDate = this.currentDate.add(1, 'day');
    this.getHistoryData(this.currentDate);
  }

  private getHistoryData(utcDate: Dayjs): void {
    if (!this.facilityId) {
      return;
    }
    const backgroundStyle = this.getBackGroundStyle();
    this.options = defaultChartOptions;
    this.options.series[0]['backgroundStyle'] = backgroundStyle;
    this.updateOptions = {
      xAxis: {
        data: [
          '0hr',
          '1hr',
          '2hr',
          '3hr',
          '4hr',
          '5hr',
          '6hr',
          '7hr',
          '8hr',
          '9hr',
          '10hr',
          '11hr',
          '12hr',
          '13hr',
          '14hr',
          '15hr',
          '16hr',
          '17hr',
          '18hr',
          '19hr',
          '20hr',
          '21hr',
          '22hr',
          '23hr',
        ],
      },
      series: [
        {
          data: [],
        },
      ],
    };
    this.showForward = utcDate.format('DD/MM/YYYY') !== dayjs().format('DD/MM/YYYY');
    const ratios: any = [];
    this.historyDataSubscription$ = this.facilitiesApiService
      .loadActivity(this.facilityId, utcDate)
      .subscribe((data: OccupationPerHour[]) => {
        data.forEach((d: OccupationPerHour) => {
          if (d && d.occupationRatio) {
            const bar = {
              value: Math.round(d.occupationRatio.ratio),
              metaData: d,
              itemStyle: {
                color: this.getRatioColor(d),
                shadowColor: this.getRatioColor(d),
                shadowBlur: 0,
                shadowOffsetX: 0,
                shadowOffsetY: 0,
              },
            };
            ratios.push(bar);
          }
        });
        this.setDataOptions(utcDate, ratios);
      });
  }

  private setDataOptions(utcDate: Dayjs, ratios: any) {
    if (this.options.series) {
      if (utcDate.format('DD/MM/YYYY') === dayjs().format('DD/MM/YYYY')) {
        this.options.tooltip = {
          trigger: 'axis',
          formatter: (params: any) => {
            return this.labelFormatter(params);
          },
        };
        this.options.series[0].data = ratios;
      }
      // const currentTimeIndex = this.options.series[0].data.length;
      // if (utcDate.format('DD/MM/YYYY') >= dayjs().format('DD/MM/YYYY')) {
      //   this.options.tooltip = {
      //     trigger: 'axis',
      //     formatter: (params: any) => {
      //       return this.labelFormatter(params);
      //     },
      //   };
      //   this.options.xAxis.updateOptions = {
      //     textStyle: {
      //       color: this.colorDark,
      //       fontSize: '12px',
      //       fontWeight: 'normal',
      //     },
      //   };
      //   this.options.xAxis.data[currentTimeIndex - 1] = {
      //     value: currentTimeIndex - 1,
      //     textStyle: {
      //       color: this.colorLight,
      //       fontSize: '12px',
      //       fontWeight: 'bolder',
      //     },
      //   };
      //   if (this.echartsInstance) {
      //     this.echartsInstance.setOption(this.options);
      //   }
      // } else {
      //   this.options.xAxis.data = {
      //     textStyle: {
      //       color: this.colorDark,
      //       fontSize: '12px',
      //       fontWeight: 'normal',
      //     },
      //   };
      //   if (this.echartsInstance) {
      //     this.echartsInstance.setOption(this.options);
      //   }
      // }
      this.updateOptions = {
        series: [
          {
            data: ratios,
          },
        ],
      };
      if (this.echartsInstance) {
        this.echartsInstance.setOption(this.updateOptions);
      }
    }
  }

  private labelFormatter(params: any) {
    const metaData = params[0].data.metaData;
    return (
      metaData.hour +
      'hr: ' +
      metaData.occupationRatio.occupation +
      ' van ' +
      metaData.occupationRatio.capacity +
      ', ' +
      metaData.occupationRatio.freeSpaces +
      ' plaatsen vrij, bezetting ' +
      this.roundToPrecision(metaData.occupationRatio.ratio, 2) +
      '%'
    );
  }

  private roundToPrecision(value: number, precision: number) {
    const multiplier = Math.pow(10, precision || 0);
    return Math.round(value * multiplier) / multiplier;
  }

  private getRatioColor(o: OccupationPerHour): string {
    if (o.occupationRatio) {
      if (o.occupationRatio.ratio < 33) {
        return this.colors[0];
      } else if (o.occupationRatio.ratio >= 33 && o.occupationRatio.ratio < 66) {
        return this.colors[1];
      } else {
        return this.colors[2];
      }
    }
    return this.colors[0];
  }

  private getBackGroundStyle() {
    const theme = this.parkbaseStorageService.getTheme();
    let backgroundStyle;
    if (theme === 'parkbaseDark') {
      backgroundStyle = {
        color: 'rgba(12, 206, 107, 0.16)',
        borderColor: '#142735',
        borderWidth: 6,
        borderType: 'solid',
      };
    } else {
      this.colorLight = '#142735';
      backgroundStyle = {
        color: '#dde2e4',
        borderColor: '#f2f6f8',
        borderWidth: 6,
        borderType: 'solid',
      };
    }

    return backgroundStyle;
  }
}
